'use client';

import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';

import styles from './RepairDetailsSection.module.scss';

interface RepairDetailsSectionProps {
  title: string;
  testId?: string;
  canChange?: boolean;
  startInEditMode?: boolean;
  isProcessing?: boolean;
  summary: ReactNode;
  children: (props: { onCancel: () => void }) => ReactNode;
}

export function RepairDetailsSection({
  title,
  testId,
  canChange = false,
  startInEditMode = false,
  isProcessing = false,
  summary,
  children,
}: Readonly<RepairDetailsSectionProps>) {
  const tReview = useTranslations('service.repairRequest.review');
  const tCommon = useTranslations('common');
  const [isEditing, setIsEditing] = useState(startInEditMode);

  const showSummary = !isEditing;
  const showChangeButton = canChange && showSummary;

  return (
    <section className={styles.section} data-testid={testId}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.card}>
        <div className={styles.content}>
          {showSummary ? summary : children({ onCancel: () => setIsEditing(false) })}
        </div>
        {showChangeButton ? (
          <Button
            type="button"
            variant="link"
            underline
            className={styles.changeButton}
            disabled={isProcessing}
            onClick={() => setIsEditing(true)}
            iconAfter={<Icon aria-hidden="true" name="chevron-right" size={14} />}
            data-testid={testId ? `${testId}-change` : undefined}
          >
            {tReview('change')}
          </Button>
        ) : null}
        {isEditing && testId ? (
          <div className={styles.actions}>
            <Button
              type="submit"
              form={`${testId}-form`}
              variant="secondary"
              loading={isProcessing}
              disabled={isProcessing}
              data-testid={testId ? `${testId}-confirm` : undefined}
            >
              {tCommon('confirm')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isProcessing}
              onClick={() => setIsEditing(false)}
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              {tCommon('cancel')}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
